
export type MentionItemType = {
  id: string;
  name: string;
  type?: string;
  label?: string;
  lang?: string;
  lang_group?: string;
  is_home_page?: boolean;
  status?: string;
  domainLabel?: string;
};

export type MessageMentionsType = {
  website?: MentionItemType;
  pages?: MentionItemType[];
  blocks?: MentionItemType[];
  elements?: MentionItemType[];
  orders?: MentionItemType[];
  products?: MentionItemType[];
};

export type VibeBlockDesignWizardLayoutType =
  | 'carousel'
  | 'grid'
  | 'split'
  | 'stack'
  | 'timeline'
  | 'marquee'
  | 'spotlight'
  | 'mosaic'
  | 'canvas'
  | 'custom';

export type VibeBlockDesignWizardMotionType =
  | 'none'
  | 'subtle'
  | 'slide'
  | 'reveal'
  | 'parallax'
  | 'tilt'
  | 'marquee'
  | 'particles'
  | 'morph'
  | 'custom';

export type VibeBlockDesignWizardPreviewType = {
  layout: VibeBlockDesignWizardLayoutType;
  motion: VibeBlockDesignWizardMotionType;
  intensity: 'low' | 'medium' | 'high';
  accent: 'image' | 'content' | 'cta' | 'motion';
  motif?: 'gallery' | 'cards' | 'character' | 'product' | 'content' | 'cta' | 'timeline' | 'abstract' | 'scene' | 'particles';
  svg?: string | null;
};

export type VibeBlockDesignWizardOptionType = {
  id: string;
  title: string;
  explanation: string;
  business_effect: string;
  preview: VibeBlockDesignWizardPreviewType;
};

export type SiteBriefChoiceOptionType = {
  title: string;
  description: string;
  // Localized tradeoff note; absent on older choices.
  caveat?: string | null;
  // A list item when the question has answer_prefix; otherwise a complete user message.
  answer: string;
  // Hex colours of a palette option, in the Weblium palette roles (light base, light accent, brand, dark accent,
  // dark base); empty for other questions.
  swatches: string[];
  // Heading and body family of a typography option.
  fonts?: { heading: string; body: string };
};

export type SiteBriefChoiceType = {
  // "options" is an ordinary question; "creativity_level" is the three-step creativity dial; "palette" proposes colour
  // schemes and "typography" font pairings, both ending in a small constructor of the person's own.
  kind: 'options' | 'creativity_level' | 'palette' | 'typography';
  question: string;
  // True when several options can apply together; false when exactly one answer is expected.
  multi_select: boolean;
  // Shared introduction ending with a colon; absent/null on legacy or single-choice cards.
  answer_prefix?: string | null;
  options: SiteBriefChoiceOptionType[];
  // The message sent for the user when they skip the question; cards saved before skipping existed omit it.
  skip_answer?: string;
  // The language Evo identified for this reply (an ISO tag); the card shows its own labels in it.
  language?: string;
};

// A font the chat's font constructor can offer, as the backend font catalog lists it.
export type SiteBriefFontCatalogEntryType = {
  family: string;
  category: 'serif' | 'sans_serif' | 'slab_serif' | 'display' | 'handwriting';
  tones: string[];
  use: 'heading' | 'body' | 'both';
  writing_systems: Array<'latin' | 'cyrillic'>;
};

export type SiteBriefSurveyQuestionType = Omit<SiteBriefChoiceType, 'kind'> & {
  id: string;
  kind: SiteBriefChoiceType['kind'] | 'free_text';
  placeholder: string | null;
  skip_answer: string;
};

export type SiteBriefSurveyType = {
  language: string;
  phase: 'clarification' | 'business' | 'design';
  skip_answer: string;
  questions: SiteBriefSurveyQuestionType[];
};

export type VibeBlockDesignWizardQuestionType = {
  wizard_id: string;
  status: 'pending';
  step: 1 | 2 | 3;
  total_steps: 3;
  question: string;
  helper_text: string;
  options: [VibeBlockDesignWizardOptionType, VibeBlockDesignWizardOptionType, VibeBlockDesignWizardOptionType];
  allow_custom: true;
};

export type VibeBlockDesignWizardAnswerType = {
  wizard_id: string;
  step: 1 | 2 | 3;
  option_id?: string;
  answer_text: string;
  custom: boolean;
  skip?: boolean;
};

export type DecisionStateType =
  | 'created'
  | 'autoApply'
  | 'declined'
  | 'outdated'
  | 'accepted'
  | 'userOwnValueSet'
  | 'userOwnValueAccepted'
  | 'userOwnValueDeclined';

export type DecisionWebsitePublishStateType =
  | 'not_applicable'
  | 'not_published'
  | 'publishing'
  | 'published';

export type OfferPlanUpgradeKindType =
  | 'userNeedsUpgradePlan'
  | 'websiteOwnerNeedsUpgradePlan';

export type OfferPlanUpgradeStateType =
  | 'upgradePlanPopupNotApplicable'
  | 'upgradePlanPopupNotShown'
  | 'upgradePlanPopupInProgress'
  | 'planUpgraded';

export type DecisionValueType = 'boolean' | 'number' | 'shortText' | 'longText';
export type DecisionAnnotationsType = {
  hide_for_enduser: boolean;
  auto_apply_decision: boolean;
};
export type DecisionUpgradeRequiredStateUpdateType =
  | 'upgradePlanPopupNotShown'
  | 'upgradePlanPopupInProgress'
  | 'planUpgraded';
export type DecisionWebsitePublishStateUpdateType = DecisionWebsitePublishStateType;
export type MessageOfferPlanUpgradeStateUpdateType = OfferPlanUpgradeStateType;

export type UpgradeRequiredOptionsType = {
  is_show_upgrade_button?: boolean;
  [key: string]: unknown;
};

export type UpgradeRequiredType = {
  websiteOwnerId: string;
  stateUpdatedAt?: string;
  options?: UpgradeRequiredOptionsType;
  [key: string]: unknown;
};

export type OfferPlanUpgradeType = {
  kind: OfferPlanUpgradeKindType;
  state: OfferPlanUpgradeStateType;
  state_updated_at?: string;
  stateUpdatedAt?: string;
  website_id?: string;
  websiteId?: string;
  website_owner_id: string;
  websiteOwnerId?: string;
};

export type MessageDecisionReferenceType = {
  id: string;
  state: DecisionStateType;
  is_upgrade_required?: boolean;
  website_publish_state?: DecisionWebsitePublishStateType;
  annotations?: DecisionAnnotationsType;
};

export type MessageRouterType = {
  groups: string[];
  reason: string;
  classifierModel: string;
  confidence: number;
  selected_tools?: string[];
};

export type DecisionListItemType = {
  id: string;
  userId: string;
  sessionId: string;
  messageId: string;
  decisionGroupId?: string;
  decisionGroupName?: string;
  websiteId?: string;
  pageId?: string;
  blockId?: string;
  elementId?: string;
  generatedAt: string;
  state: DecisionStateType;
  annotations?: DecisionAnnotationsType;
  website_publish_state?: DecisionWebsitePublishStateType;
  decisionType: string;
  decisionSubType: string;
  decision_label: string;
  is_upgrade_required?: boolean | UpgradeRequiredType;
  upgradeRequired?: UpgradeRequiredType;
  upgrade_required?: UpgradeRequiredType;
  valueType: DecisionValueType;
  value_boolean?: boolean;
  value_number?: number;
  value_short_text?: string;
  value_long_text?: string;
  appliedAt: string | null;
  declinedAt: string | null;
  outdatedAt: string | null;
  history: Array<{
    message: string;
    date: string;
  }>;
  createdAt: string;
  updatedAt: string;
};

export type ChatCompletionMentionsType = {
  website: {
    id: string | null;
    name: string;
    status: string;
    domainLabel: string;
  };
  pages: MentionItemType[];
  blocks: MentionItemType[];
  elements: MentionItemType[];
  orders: MentionItemType[];
  products: MentionItemType[];
};

export type ChatMessage = {
  id: string | number;
  role: string;
  content: string;
  images?: ClientImageData[];
  mentions?: MessageMentionsType;
  router?: MessageRouterType;
  decisions?: MessageDecisionReferenceType[];
  offer_plan_upgrade?: OfferPlanUpgradeType;
  vibe_block_design_wizard_question?: VibeBlockDesignWizardQuestionType;
  vibe_block_design_wizard_answer?: VibeBlockDesignWizardAnswerType;
};

export type WebliumWebsiteMentionItem = {
  id: string;
  name: string;
  subdomain: string;
  externalDomain: string | null;
  internalDomain: string;
  domainLabel: string;
  status: 'unpublished' | 'published';
  favicon: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type CustomCodeSectionKey = 'head' | 'afterBody' | 'beforeBody' | 'css';

export type CustomCodeSectionsType = Record<CustomCodeSectionKey, string>;

export type EmbedCodeSectionKey = 'html' | 'cssLess' | 'js';

export type EmbedCodeSectionsType = Record<EmbedCodeSectionKey, string>;

export type EmbedCodeDataType = {
  sections: EmbedCodeSectionsType;
  isolateStyles: boolean;
};

export type AdminBlockContentViewType =
  | 'flatElements'
  | 'treeElements'
  | 'properties'
  | 'fullContent'
  | 'rawContent';

export type AdminBlockContentSummaryType = {
  websiteId: string;
  pageId: string;
  blockId: string;
  flatElementsListItemsCount: number;
};

export type AdminBlockContentPushResponseType = {
  status: 'OK';
  message: string;
  websiteId: string;
  pageId: string;
  blockId: string;
};

export type AdminTextElementType = {
  websiteId: string;
  pageId: string;
  blockId: string;
  elementId: string;
  type: string;
  content: string;
  modified?: boolean;
};

export type TextElementQuickActionType = 'make_shorter' | 'make_longer' | 'rewrite';

export type TextElementQuickActionResponseType = {
  action: TextElementQuickActionType;
  text: string;
  websiteId: string;
  pageId: string;
  blockId: string;
  elementId: string;
};

export type AgentUserRoleType =
  | 'user'
  | 'supportEngineer'
  | 'qaEngineer'
  | 'frontendDeveloper'
  | 'promptEngineer'
  | 'aiManagerRO'
  | 'aiManagerRW'
  | 'admin';

export type SiteBriefStatusType =
  | 'init'
  | 'gather_requirements'
  | 'suggest_requirements'
  | 'build_site_structure'
  | 'generate_site_core'
  | 'fulfill_vibe_blocks';
export type SiteBriefRequirementStatusType = 'active' | 'disabled';

export type SiteBriefOwnerType = {
  id: string;
  email: string;
  name: string;
  role: AgentUserRoleType;
};

export type SiteBriefSessionSummaryType = {
  id: string;
  name: string;
  owner: SiteBriefOwnerType | null;
  is_active: boolean;
  is_ended: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
};

export type SiteBriefListItemType = {
  can_stop?: boolean;
  can_delete?: boolean;
  task?: SiteBriefTaskType | null;
  id: string;
  session_id: string;
  weblium_user_id: string;
  version: number;
  requirements_revision?: number;
  weblium_website_id: string | null;
  status: SiteBriefStatusType;
  requirements_count: number;
  sessions: SiteBriefSessionSummaryType[];
  created_at: string;
  updated_at: string;
};

export type SiteBriefListScopeType = 'own' | 'all';

export type SiteBriefListResponseType = {
  status: 'OK';
  scope: SiteBriefListScopeType;
  totals: Record<SiteBriefListScopeType, number>;
  site_briefs: SiteBriefListItemType[];
};

export type SiteBriefBuildProgress = {
  current_stage: SiteBriefStatusType | null;
  stages: Array<{ stage: SiteBriefStatusType; state: 'pending' | 'running' | 'completed' | 'failed'; attempts: number; error: string | null }>;
  next_retry_at: string | null;
  blocks: { total: number; completed: number; running: number; failed: number };
};

export type SiteBriefTaskType = {
  build?: SiteBriefBuildProgress | null;
  id: string;
  operation: 'conversation' | 'initialize' | 'gather_requirements' | 'suggest_requirements' | 'update_requirements'
    | 'build_site_structure' | 'generate_site_core' | 'fulfill_vibe_blocks'
    | 'return_to_gather_requirements' | 'return_to_suggest_requirements' | 'build_site';
  user_message_id?: string | null;
  reply_message_id?: string | null;
  queue_key: string;
  state: 'queued' | 'running' | 'cancelling' | 'cancelled' | 'completed' | 'failed';
  attempts: number;
  error: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  finished_at: string | null;
};

export type SiteBriefChatMetadata = {
  // Evo has finished the interview; the brief sidebar is shown from then on.
  is_gather_requirements_done?: boolean;
  id: string;
  status: SiteBriefStatusType;
  version: number;
  can_stop?: boolean;
  active_task_id?: string | null;
  can_build?: boolean;
  can_retry?: boolean;
  creation?: {
    weblium_website_id: string | null;
    creation_status: string;
    fulfillment_status: string;
    completed_blocks: number;
    total_blocks: number;
  } | null;
  presentation?: SiteBriefPresentationType | null;
};

// The person's colours for the summary: the chosen palette by Weblium role and the exact colours they named.
export type SiteBriefPresentationColorsType = {
  // In this role order; null until a palette is chosen or composed.
  palette: Array<{ role: 'light_base' | 'light_accent' | 'brand' | 'dark_accent' | 'dark_base'; hex: string }> | null;
  required: Array<{ hex: string; usage: string }>;
};

export type SiteBriefPresentationType = {
  language: string;
  summary: string;
  source_requirements_revision: number;
  requirements: Array<{
    source_ids: string[];
    label: string;
    value: string;
    status: 'confirmed' | 'suggested' | 'needs_clarification';
  }>;
  colors: SiteBriefPresentationColorsType;
};

export type SiteBriefConversationReceipt = {
  status: 'OK';
  task: SiteBriefTaskType;
  user_message_id: string;
  site_brief: SiteBriefChatMetadata;
};

export type SiteBriefTaskResponse = {
  status: 'OK';
  task: SiteBriefTaskType | null;
  lastReply: GetSessionResponse['session']['messages'][number] | null;
  site_brief: SiteBriefChatMetadata;
};

export type SiteBriefRequirementType = {
  key: string;
  id: string;
  value: unknown;
  strength: string;
  origin: string;
  status: SiteBriefRequirementStatusType;
  confidence: number;
  scope: { kind: string; [key: string]: unknown };
  kind: string;
  evidence: {
    session_id: string;
    message_id: string;
    quote: string;
  };
};

export type SiteBriefRequirementEventType = SiteBriefRequirementType & {
  time: string;
  merge_policy: string;
};

export type SiteBriefType = SiteBriefListItemType & {
  init_prompt: string;
  requirements_revision: number;
  weblium_website_id: string | null;
  requirements: SiteBriefRequirementType[];
  presentation?: SiteBriefPresentationType | null;
};

export type SiteStructureApplicationType = {
  key: string;
  application: 'analytics' | 'chat' | 'contact-form' | 'custom-code' | 'ecommerce' | 'integrations' | 'social-icons' | 'blog';
  path: string | null;
  robots: { allow: string[]; disallow: string[] };
  requires_configuration: boolean;
  properties: Array<{ key: string; value: string | number | boolean | null }>;
};

export type SiteStructurePageType = {
  key: string;
  lang_group_key: string;
  parent_page_key: string | null;
  website_application_key: string | null;
  lang: string;
  roles: string[];
  layout: string | null;
  sort_order: number;
  metadata: {
    display_name: string;
    slug: string;
    homepage: boolean;
    hide_header: boolean;
    hide_footer: boolean;
    seo_title: string | null;
    seo_description: string | null;
    keywords: string[];
    no_index: boolean;
    no_follow: boolean;
    canonical_url: string | null;
  };
  blocks: SiteStructureBlockType[];
};

export type SiteStructureBlockType = {
  key: string;
  document: {
    name: string;
    anchor_name: string | null;
    role: string | null;
    order: number;
    state: 'normal';
  };
  build: {
    purpose: string;
    editor_kind: string;
    source_block_id: string | null;
    source_block_version: number | null;
    layout: string | null;
    content_goal: string;
    prompt: string;
    required_elements: string[];
    form_key: string | null;
    surface_emphasis: 'base' | 'secondary' | 'contrast';
    background_token: 'light_shade' | 'light_accent' | 'dark_shade' | 'dark_accent' | 'brand';
    background_type: 'solid' | 'gradient';
    spacing_tier: 'regular' | 'feature';
    pattern: string;
  };
};

export type SiteStructureGlobalBlockType = SiteStructureBlockType & {
  placement: 'header' | 'footer' | 'body';
  lang: string;
};

export type SiteStructureType = {
  id: string;
  user_id: string;
  weblium_user_id: string;
  session_id: string;
  site_brief_id: string;
  schema_version: string;
  theme: 'light' | 'dark';
  version: number;
  source_site_brief_version: number;
  source_requirements_revision: number;
  source_requirement_ids: string[];
  is_stale: boolean;
  stale_reason?: string | null;
  expected_schema_version?: string;
  created_at: string;
  updated_at: string;
  website: {
    name: string;
    description: string | null;
    subdomain: string;
    status: 'unpublished';
    favicon_url: string | null;
    seo: {
      title: string | null;
      description: string | null;
      keywords: string[];
      no_index: boolean;
      no_follow: boolean;
      locale: string;
      country: string | null;
      business_area: string | null;
      time_zone: string | null;
    };
    languages: Array<{ key: string; lang: string; flag: string | null; is_default: boolean }>;
    redirects: Array<{ from: string; to: string; status_code: 301 | 302 }>;
  };
  uikit: {
    palette: { primary: string; secondary: string; accent: string; dark: string; light: string };
    typography: { styles: Array<Record<string, string | number>> };
    buttons: {
      font_family: string;
      font_weight: number;
      text_transform: string;
      variants: Array<Record<string, string | number>>;
    };
    layout: {
      unit_px: 4 | 6 | 8 | 10;
      header_height_px: number;
      alignment: { section_header: 'left' | 'center' };
    };
    logo: {
      wordmark: string;
      mark: string;
      monogram: string;
      casing: 'as_written' | 'lowercase' | 'uppercase';
      tracking: 'tight' | 'normal' | 'wide';
      accent_part: 'none' | 'first_word' | 'last_word';
    };
  };
  applications: SiteStructureApplicationType[];
  pages: SiteStructurePageType[];
  global_blocks: SiteStructureGlobalBlockType[];
  navigation: Array<{
    key: string;
    placement: 'header' | 'footer';
    lang: string;
    items: Array<{
      key: string;
      parent_item_key: string | null;
      label: string;
      page_key: string | null;
      external_url: string | null;
      open_in_new_tab: boolean;
      sort_order: number;
    }>;
  }>;
  forms: Array<{
    key: string;
    name: string;
    page_key: string | null;
    purpose: string;
    field_types: string[];
    submit_action: string;
    integration_keys: string[];
  }>;
  popups: Array<{
    key: string;
    name: string;
    purpose: string;
    trigger: string;
    block_keys: string[];
  }>;
  integrations: Array<{
    key: string;
    provider: string;
    kind: string;
    requires_configuration: boolean;
  }>;
  ecommerce: {
    website_application_key: string;
    mode: string;
    currency: string;
    product_types: string[];
    page_keys: string[];
    categories: Array<{ key: string; parent_category_key: string | null; title: string; slug: string }>;
    payment_gateways: Array<{ provider: string; mode: string; requires_configuration: true }>;
    shipping_methods: Array<{ requirement: string; weblium_type: string; requires_configuration: boolean }>;
  } | null;
  blog: {
    website_application_key: string;
    mode: 'weblium_blog_app';
    homepage_lang: string;
    feed_page_key: string;
    routes: { root: string; post: string };
  } | null;
};

export type SiteCreationStatusType =
  | 'preparing'
  | 'prepared'
  | 'uploading'
  | 'committing'
  | 'completed'
  | 'failed'
  | 'abandoned'
  | 'unverified';

export type RenderedSiteType = {
  id: string;
  weblium_website_id: string | null;
  weblium_user_id?: string;
  creation_status: SiteCreationStatusType;
  fulfillment_status?: 'pending' | 'running' | 'completed' | 'failed';
  fulfillment_error?: string | null;
  fulfilled_at?: string | null;
  fulfilled_block_keys?: string[];
  vibe_blocks?: Array<{ key: string; prompt: string; lang: string; page_id: string | null; block_ids: string[] }>;
  schema_version?: string;
  version?: number;
  source_site_brief_version?: number;
  source_requirements_revision?: number;
  source_site_structure_version?: number;
  prepared_at?: string;
  weblium_started_at?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
  manifest_hash?: string;
  is_stale: boolean;
  error?: { code: string; message: string; recoverable?: boolean } | null;
  deferred_requirements?: Array<{
    id: string;
    key: string;
    fulfilled: false;
    reason: string;
  }>;
  website?: Record<string, unknown>;
  uikitpalettes?: Array<Record<string, unknown>>;
  uikittypographies?: Array<Record<string, unknown>>;
  uikitbuttons?: Array<Record<string, unknown>>;
  websiteapplications?: Array<Record<string, unknown>>;
  websitepages?: Array<Record<string, unknown>>;
  blocks?: Array<Record<string, unknown>>;
  resources?: Array<Record<string, unknown>>;
};

export type SiteStructureReadinessType = {
  ready: boolean;
  missing_requirement_keys: string[];
  blocking_open_question_ids: string[];
  unsupported_requirements: Array<{ key: string; reason: string }>;
};

export type SiteBriefRequirementDefinitionType = {
  key: string;
  value_schema: Record<string, unknown>;
  cardinality: string;
  scopes: string[];
  merge: string;
  conflict: string;
  affects: string[];
  inference: string;
  always_include_in_prompt: boolean;
  validator_id?: string;
  capability_id?: string;
  description?: string;
};

export type SiteBriefRegistryOptionsType = {
  brief_statuses: SiteBriefStatusType[];
  requirement_cardinalities: string[];
  requirement_strengths: string[];
  requirement_origins: string[];
  requirement_statuses: SiteBriefRequirementStatusType[];
  scope_kinds: string[];
  requirement_kinds: string[];
  requirement_kind_descriptions: Record<string, string>;
  merge_policies: string[];
  merge_policy_descriptions: Record<string, string>;
};

export type SiteBriefRequirementInputType = {
  key: string;
  value: unknown;
  strength: string;
  origin: string;
  status: SiteBriefRequirementStatusType;
  confidence: number;
  scope: { kind: string; [key: string]: unknown };
  kind: string;
  evidence: {
    session_id: string;
    message_id: string;
    quote: string;
  };
  merge_policy: string;
};

export type WebliumWebsitePageMetadata = {
  parentId?: string;
  displayName?: string;
  slug?: string;
  homepage?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
};

export type WebliumWebsitePageMentionItem = {
  id: string;
  name: string;
  label: string;
  lang?: string;
  lang_group?: string;
  is_home_page: boolean;
  is_hidden: boolean;
  metadata: WebliumWebsitePageMetadata;
};

export type WebliumWebsiteBlockMentionItem = {
  id: string;
  name: string;
  label: string;
  roles?: string[];
};

export type WebliumWebsiteElementMentionItem = {
  id: string;
  name: string;
  label: string;
  type?: string;
};

export type AvailableToolsListType = {
  name: string;
  description: string;
};

export type SystemEnvConfigResponse = {
  status: 'OK';
  envName: string;
  envComment: string;
};

export type SystemConnectionHoldTestResponse = {
  status: 'OK';
  message: string;
  requestedDelayMs: number;
  actualDurationMs: number;
  startedAt: string;
  finishedAt: string;
};

export type SystemWebliumAuthCheckResponse = {
  status: 'OK';
  message: string;
  endpoint: string;
  upstreamStatus: number;
  user: {
    id: string | null;
    username: string | null;
    role: string | null;
  };
};

export const PAGE_NAME_OPTIONS = [
  'websites-list',
  'site-collections',
  'trash',
  'domains-list',
  'referrals',
  'statistics',
  'general-info',
  'domains',
  "forms",
  'ecommerce',
  'blog',
  'multilingual',
  'crm',
  'social-links',
  'cookie-banner',
  'analytics',
  'integrations',
  'chat',
  'custom-code',
  'contributors',
  'subscription',
  'editor'
] as const;

export type PageNameOption = typeof PAGE_NAME_OPTIONS[number];

export type SessionContextType = {
  websiteId: string | null;
  websiteName: string | null;
  pageName: PageNameOption | '' | null;
};

export type SessionType = 'general' | 'vibe_block' | 'vibe_site';
export type CreatableSessionType = 'general' | 'vibe_block' | 'vibe_site';

export type SessionVibeBlockType = {
  website_id: string;
  website_name: string | null;
  page_id: string;
  page_name: string | null;
  block_id: string;
  block_name: string | null;
};

export type CreateSessionVibeBlockPayload = {
  website_id: string;
  page_id: string;
  block_id: string;
};

export type CreateSessionOptions = {
  init_prompt?: string;
  start_gathering?: boolean;
  type?: CreatableSessionType;
  websiteId?: string | null;
  pageName?: PageNameOption | '' | null;
  vibe_block?: CreateSessionVibeBlockPayload | null;
};

export type SessionSummary = {
  text: string;
  timestamp: string;
};

export type SessionRenameOptions = {
  timestamp: string;
  userMessagesUsed: number;
  regenerationCount: number;
};

export type SessionUsageInfo = {
  limitClass: string;
  messagesUsed: number;
  messagesRemaining: number;
  maxMessages: number;
  inputTokensUsed: number;
  inputTokensRemaining: number;
  maxInputTokens: number;
  outputTokensUsed: number;
  outputTokensRemaining: number;
  maxOutputTokens: number;
};

export type ChatCompletionResponse = {
  task?: SiteBriefTaskType | null;
  status: 'OK' | 'Error';

  // messages: {
  //   role: string;
  //   content: string;
  // }[];

  lastToolsCalls: completionFunctionalToolsCallsLogType[];

  totalTokens: number;
  lastReply: {
    id: string;
    role: 'user' | 'assistant' | 'tool';
    content: string;
    timestamp: string;
    duration: number;
    is_liked: boolean;
    is_disliked: boolean;
    mentions?: MessageMentionsType;
    decisions?: MessageDecisionReferenceType[];
    offer_plan_upgrade?: OfferPlanUpgradeType;
    vibe_block_design_wizard_question?: VibeBlockDesignWizardQuestionType;
  };
  router?: MessageRouterType;
  mentions: ChatCompletionMentionsType;
  decisions: MessageDecisionReferenceType[];
  currentContext: SessionContextType;

  isConversationEnded: boolean;
  result: object;
};

export type completionFunctionalToolsCallsLogType = {
  id: string;
  name: string;
  arguments: string;
  result: string;
};

// Sessions types
export type SessionVibeSiteType = {
  site_brief_status: SiteBriefStatusType;
  site_brief_active_task_id: string | null;
  site_brief_active_task_state: SiteBriefTaskType['state'] | null;
  site_brief_active_task_operation: SiteBriefTaskType['operation'] | null;
};

export type SessionListItem = {
  id: string;
  name: string;
  type: SessionType;
  vibe_block: SessionVibeBlockType | null;
  vibe_site: SessionVibeSiteType | null;
  is_active: boolean;
  is_ended: boolean;
  created_at: string;
  updated_at: string;
  language?: string;
};

export type SessionsListResponse = {
  status: 'OK';
  sessions: SessionListItem[];
};

export type CreateSessionResponse = {
  status: 'OK';
  message: string;
  session: {
    id: string;
    name: string;
    type: SessionType;
    vibe_block: SessionVibeBlockType | null;
    vibe_site: SessionVibeSiteType | null;
    site_brief?: SiteBriefChatMetadata | null;
    language?: string;
  };
};

// Image data returned from backend for display
export type ClientImageData = {
  url: string;
  name?: string;
};

export type GetSessionResponse = {
  status: 'OK';
  session: {
    id: string;
    name: string;
    type: SessionType;
    vibe_block: SessionVibeBlockType | null;
    vibe_site: SessionVibeSiteType | null;
    site_brief?: SiteBriefChatMetadata | null;
    language?: string;
    current_context: SessionContextType | null;
    context_history: SessionContextType[];
    messages: {
      id: string;
      role: 'user' | 'assistant' | 'tool';
      content: string;
      timestamp: string;
      duration: number;
      is_liked: boolean;
      is_disliked: boolean;
      images?: ClientImageData[]; // Images attached to user messages
      mentions?: MessageMentionsType;
      router?: MessageRouterType;
      decisions?: MessageDecisionReferenceType[];
      offer_plan_upgrade?: OfferPlanUpgradeType;
      vibe_block_design_wizard_question?: VibeBlockDesignWizardQuestionType;
      vibe_block_design_wizard_answer?: VibeBlockDesignWizardAnswerType;
      // Every Site Brief question arrives as a group; a single card is a group of one question.
      site_brief_survey?: SiteBriefSurveyType;
    }[];
    tools: string[];
    totalTokens: number;
    summary: SessionSummary | null;
    renameOptions: SessionRenameOptions | null;
    usage: SessionUsageInfo | null;
    is_active: boolean;
    is_ended: boolean;
    is_deleted: boolean;
    created_at: string;
    updated_at: string;
  };
  lastToolsCalls: completionFunctionalToolsCallsLogType[];
  lastReply: {
    role: string;
    content: string;
    suggestions: {
      type: 'multiChoice' | 'singleChoice' | '';
      options: string[];
    };
    isConversationEnded: boolean;
  };
};

export type DecisionsListResponse = {
  status: 'OK';
  decisions: DecisionListItemType[];
};

export type DecisionActionResponse = {
  status: 'OK';
  decision: DecisionListItemType;
  messageDecision: MessageDecisionReferenceType;
};

export type MessageOfferPlanUpgradeActionResponse = {
  status: 'OK';
  message: string;
  messageOfferPlanUpgrade: OfferPlanUpgradeType;
};
